import { intersection, isEmpty, uniq } from "lodash-es"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import StaticSelector from "components/Atoms/StaticSelector"
import FormController from "components/Molecules/FormController"
import ConfirmButtonMutation from "components/Templates/ConfirmButtonMutation"
import CustomTimePicker from "components/Templates/form/CustomTimePicker"
import AppToaster from "utils/AppToaster"
import {
  EDIT_ORDER_STATUSES,
  ORDER_STATUSES,
  ORDER_STATUS_FLOW,
} from "data/orderStatuses"
import { BULK_UPDATE_ORDERS } from "graphql/order/order.mutations"
import { CLOSE_BULK_UPDATE_DIALOG } from "store/order.redux"
import { RESET_SELECTED } from "store/selector.redux"
import unique from "utils/unqiue"
const BulkUpdateDialog = () => {
  const selectedOrder = useSelector(state => state.orders.selectedOrder)
  const selectedOrders = useSelector(state => state.selector?.orders?.selected)
  const [state, setState] = useState({})
  const isOpen = useSelector(state => state.orders.isBulkDialogOpen)
  const dispatch = useDispatch()

  useEffect(() => {
    setState({
      status: ORDER_STATUSES.find(o => o?.id === selectedOrder?.orderStatus),
      estimatedDelivery:
        selectedOrder?.orderItems?.[0]?.shipping?.estimatedDelivery,
    })
  }, [selectedOrder])

  function handleChange(path, value) {
    setState(prev => ({
      ...prev,
      [path]: value,
    }))
  }

  const availableSingleStatuses = useMemo(() => {
    // Take the current status and append with the the order status flow and extract all the unique values
    const statuses = unique(
      [ORDER_STATUSES.find(o => o?.id === selectedOrder?.orderStatus)]
        ?.concat(
          ORDER_STATUS_FLOW?.[selectedOrder?.orderStatus]?.map(s =>
            EDIT_ORDER_STATUSES.find(o => o?.id === s)
          )
        )
        ?.filter(s => s)
    )
    return selectedOrder?.isConsolidated
      ? statuses.filter(s => s?.id !== "CBV")
      : statuses
  }, [selectedOrder])

  const availableBulkStatuses = useMemo(() => {
    // 1. Get list of all the statuses
    const allStatuses =
      selectedOrders && Object.values(selectedOrders)?.map(o => o?.orderStatus)

    // 2. Grab all the unique statuses and map them according to the order status flow and remove falsies
    const uniqueStatus = uniq(allStatuses)
      ?.map(s => ORDER_STATUS_FLOW?.[s])
      ?.filter(s => s)

    // 3. Extract all the possible common statuses the user can change to and remove falsies
    const commonOrderStatuses = intersection(...uniqueStatus)
      ?.map(s => EDIT_ORDER_STATUSES.find(st => st.id === s))
      ?.filter(s => s)

    // 4. Check for merged order
    const isAnyOrderConsolidated =
      selectedOrders &&
      Object.values(selectedOrders).find(s => s.isConsolidated)

    const statuses = isAnyOrderConsolidated
      ? commonOrderStatuses.filter(s => s.id !== "CBV")
      : commonOrderStatuses
    return statuses
  }, [selectedOrders])

  const ordersArray = selectedOrder?.id
    ? [selectedOrder?.id]
    : !isEmpty(selectedOrders)
    ? Object.keys(selectedOrders)
    : []

  return (
    <CustomDialog
      light
      icon="th-list"
      width="35%"
      isOpen={isOpen}
      title={
        selectedOrder?.id ? (
          <span className="vt-muted">
            Update order -{" "}
            <span className="vt-dark">{selectedOrder?.trackingCode}</span>
          </span>
        ) : (
          <span className="vt-muted">
            Update{" "}
            <span className="vt-dark">
              {selectedOrders && Object.keys(selectedOrders)?.length}
            </span>{" "}
            orders
          </span>
        )
      }
      onClose={() =>
        dispatch({
          type: CLOSE_BULK_UPDATE_DIALOG,
        })
      }
      footerRightButton={
        <ConfirmButtonMutation
          addConfirmation={state?.status?.id === "CBV"}
          alert={
            <span>
              Are you sure you want to change statuses of {ordersArray.length}{" "}
              orders to <strong>Rejected</strong> ?
            </span>
          }
          validate={state => {
            if (!state?.status && !state?.estimatedDelivery) {
              AppToaster.show({
                message: "Please enter atleast one of the fields",
                intent: "danger",
                icon: "cross",
              })
              return false
            }
            if (!state?.orders || state?.orders?.length === 0) {
              AppToaster.show({
                message: "Please select atleast 1 order",
                intent: "warning",
                icon: "warning-sign",
              })
              return false
            }
            return true
          }}
          icon="confirm"
          text="Submit"
          onError={() => {
            AppToaster.show({
              message: "Unable to update orders",
              intent: "danger",
              icon: "cross",
            })
          }}
          mutation={BULK_UPDATE_ORDERS}
          onCompleted={({ bulkUpdateOrder }) => {
            AppToaster.show({
              message: `${bulkUpdateOrder?.order?.length} orders updated successfully`,
              intent: "success",
              icon: "tick",
            })

            dispatch({
              type: RESET_SELECTED,
              path: "orders",
            })
            dispatch({
              type: CLOSE_BULK_UPDATE_DIALOG,
            })
          }}
          variables={{
            orders: ordersArray,
            status: state?.status?.id,
            estimatedDelivery: state?.estimatedDelivery,
          }}
        />
      }
    >
      <FormController source={state} onChange={handleChange}>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-6">
            <StaticSelector
              absolute
              label="Order Status"
              items={
                selectedOrder?.id
                  ? availableSingleStatuses
                  : availableBulkStatuses
              }
              path="status"
            />
          </div>
          <div className="col-span-6">
            <CustomFormGroup label="Estimated Delivery Time">
              <CustomTimePicker fill absolute path="estimatedDelivery" />
            </CustomFormGroup>
          </div>
        </div>
      </FormController>
    </CustomDialog>
  )
}

export default BulkUpdateDialog
